import './instrumentation.ts';
import { addAppBreadcrumb } from '@/common/telemetry/sentry.ts';
import { router } from '@/router.ts';
import { queryClient } from '@/queryClient.ts';

import { createTheme, MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { QueryClientProvider, type QueryKey } from '@tanstack/react-query';

import { RouterProvider } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import './index.css';
import { PostHogProvider } from '@posthog/react';
import DevtoolsProvider from '@/devtools/DevtoolsProvider.tsx';
import {
  getSelectiveAutocaptureConfig,
  preparePostHogEvent,
  resolvePostHogRuntime,
} from '@/common/analytics/posthog-config.ts';
import type { PostHogConfig } from 'posthog-js';

// *** ERROR EVENT IDS *** //
const queryErrorEvents = new Map<string, string>();

export function hasQueryErrorEventId(queryKey: QueryKey) {
  return queryErrorEvents.has(JSON.stringify(queryKey));
}

export function getQueryErrorEventId(queryKey: QueryKey) {
  return queryErrorEvents.get(JSON.stringify(queryKey));
}

export function setQueryErrorEventId(
  queryKey: QueryKey,
  eventId: string | undefined,
) {
  if (eventId === undefined) return;
  return queryErrorEvents.set(JSON.stringify(queryKey), eventId);
}

export function clearQueryErrorEventId(queryKey: QueryKey) {
  queryErrorEvents.delete(JSON.stringify(queryKey));
}

const theme = createTheme({
  defaultRadius: 0,
});

const postHogRuntime = resolvePostHogRuntime({
  hostname: window.location.hostname,
  mode: import.meta.env.MODE,
  configuredEnvironment: import.meta.env.VITE_PUBLIC_POSTHOG_ENVIRONMENT,
  captureLocal: import.meta.env.VITE_PUBLIC_POSTHOG_CAPTURE_LOCAL,
});

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
  autocapture: postHogRuntime.enabled ? getSelectiveAutocaptureConfig() : false,
  capture_pageview: 'history_change',
  capture_pageleave: true,
  mask_all_text: false,
  disable_session_recording: !postHogRuntime.enabled,
  before_send: (event) => preparePostHogEvent(event, postHogRuntime),
} as const satisfies Partial<PostHogConfig>;

createRoot(document.getElementById('root')!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((_error, errorInfo) => {
    addAppBreadcrumb({
      category: 'react.error',
      message: 'Uncaught React error',
      level: 'error',
      data: { stack: errorInfo.componentStack },
    });
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <PostHogProvider
    apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN}
    options={options}
  >
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Notifications />
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <DevtoolsProvider />
      </QueryClientProvider>
    </MantineProvider>
  </PostHogProvider>,
);
